import { isValidCPF } from '@/models/cpf'
import { isValidCNPJ, sanitizeCNPJ } from '@/models/cnpj'

const CNPJ_LENGTH = 14

export const VEHICLE_COLUMNS = [
  { key: 'plate', field: 'plate', label: 'Placa' },
  { key: 'renavam', field: 'renavam', label: 'Renavam' },
  { key: 'chassi', field: 'chassi', label: 'Chassi' },
  { key: 'uf', field: 'uf', label: 'UF' },
  { key: 'cityCode', field: 'city_code', label: 'Código da cidade' },
  { key: 'cpfCnpj', field: 'cpf_cnpj', label: 'CPF/CNPJ' },
  { key: 'securityCode', field: 'security_code', label: 'Código de segurança' },
  { key: 'fleetCode', field: 'fleet_code', label: 'Código da frota' },
  { key: 'driver', field: 'driver', label: 'Motorista' },
  { key: 'brand', field: 'brand', label: 'Marca' },
  { key: 'model', field: 'model', label: 'Modelo' },
  { key: 'kind', field: 'kind', label: 'Tipo' },
  { key: 'application', field: 'application', label: 'Aplicação' },
  { key: 'cmCrono', field: 'cm_crono', label: 'Cronotacógrafo' },
  { key: 'sneModule', field: 'sne_module', label: 'Módulo SNE' },
]

export const LINE_FIELD = 'line'

export const BATCH_HEADER = [LINE_FIELD, ...VEHICLE_COLUMNS.map(column => column.field)]

export const VALID_UFS = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]

const CHASSI_REQUIRED_UFS = ['MG', 'TO']
const SECURITY_CODE_REQUIRED_UF = 'SE'
const SNE_MODULES = ['20', '40']

const REGEX_OLD_PLATE = /^[A-Z]{3}\d{4}$/
const REGEX_MERCOSUL_PLATE = /^[A-Z]{3}\d[A-Z]\d{2}$/

export function sanitizeRenavam(renavam) {
  const digits = String(renavam || '').replace(/\D/g, '')
  return digits ? digits.padStart(11, '0') : ''
}

export function isValidRenavam(renavam) {
  const digits = sanitizeRenavam(renavam)
  if (digits.length !== 11) return false

  const reversed = digits.slice(0, 10).split('').reverse()
  let accumulator = 0

  for (let i = 0; i < 8; i++) accumulator += parseInt(reversed[i]) * (i + 2)
  accumulator += parseInt(reversed[8]) * 2
  accumulator += parseInt(reversed[9]) * 3

  let digit = 11 - (accumulator % 11)
  if (digit >= 10) digit = 0

  return digit === parseInt(digits[10])
}

export function isValidPlate(plate) {
  const value = String(plate || '').replace(/\s+/g, '').toUpperCase()
  return REGEX_OLD_PLATE.test(value) || REGEX_MERCOSUL_PLATE.test(value)
}

export function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '')
}

export function sanitizeDocument(value) {
  const document = sanitizeCNPJ(value)
  return document.length === CNPJ_LENGTH ? document : onlyDigits(value)
}

export function buildBatchRow(row) {
  return [
    row._line,
    ...VEHICLE_COLUMNS.map(column => {
      const value = row[column.key]
      return value === null || value === undefined ? '' : String(value).trim()
    }),
  ]
}

export function vehicleRowErrors(row, knownCnpjs) {
  const errors = []
  const plate = String(row.plate || '').replace(/\s+/g, '').toUpperCase()
  const renavam = sanitizeRenavam(row.renavam)
  const uf = String(row.uf || '').replace(/\s+/g, '').toUpperCase()
  const document = sanitizeDocument(row.cpfCnpj)
  const sneModule = String(row.sneModule || '').trim()

  if (!plate) errors.push('Placa é obrigatória')
  else if (!isValidPlate(plate)) errors.push('Placa inválida')

  if (!renavam) errors.push('Renavam é obrigatório')
  else if (!isValidRenavam(renavam)) errors.push('Renavam inválido')

  if (!document) errors.push('CPF/CNPJ é obrigatório')
  else if (document.length === CNPJ_LENGTH) {
    if (!isValidCNPJ(document)) errors.push('CNPJ inválido')
    else if (knownCnpjs && !knownCnpjs.has(document)) errors.push('Empresa não encontrada com esse CNPJ')
  } else if (!isValidCPF(document)) errors.push('CPF inválido')

  if (!uf) errors.push('UF é obrigatória')
  else if (!VALID_UFS.includes(uf)) errors.push('UF inválida')
  else if (CHASSI_REQUIRED_UFS.includes(uf) && !String(row.chassi || '').trim())
    errors.push(`Chassi é obrigatório para UF ${uf}`)
  else if (uf === SECURITY_CODE_REQUIRED_UF && !String(row.securityCode || '').trim())
    errors.push('Código de segurança é obrigatório para UF SE')

  if (sneModule && !SNE_MODULES.includes(sneModule))
    errors.push('Módulo SNE deve ser 20, 40 ou ficar vazio')

  return errors
}
